// askSmart-ui/src/pages/TOSP/tabs/EstoppelsTab.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import DeleteConfirmModal from "../../../components/DeleteConfirmModal";
import { UploadIcon } from "../../../components/UploadIcon";
import { useAuthentication } from "../../../context/AuthContext";
import EstoppelTemplateEditor from "../components/EstoppelTemplateEditor";
import { EstoppelDraft, estoppelDraft, estoppelFileName } from "../estoppelValues";
import { formatDate } from "../format";
import {
  deleteEstoppel,
  EstoppelCertificate,
  EstoppelState,
  estoppelFileUrl,
  estoppelTemplateUrl,
  generateEstoppels,
  loadEstoppels,
  openEstoppelYear,
  sendEstoppels,
  uploadEstoppelTemplate,
} from "../tospApi";
import { useTospData } from "../TospDataContext";
import { Tenant } from "../types";

/** Sent a few at a time, so no single request runs long enough to time out. */
const BATCH_SIZE = 10;

type View = "template" | number;

interface Row {
  tenant: Tenant;
  draft: EstoppelDraft;
  certificate?: EstoppelCertificate;
}

/**
 * A certificate's history, newest first. Records from before history was kept
 * carry only the latest generated / sent stamps, so one is built from those.
 */
function historyOf(cert: EstoppelCertificate) {
  const list = cert.history?.length
    ? cert.history
    : [
        { at: cert.generatedAt, byName: cert.generatedByName, status: "Generated", fileName: cert.fileName },
        ...(cert.sentAt ? [{ at: cert.sentAt, byName: cert.sentByName, status: "Sent to SMRT Docs", fileName: cert.fileName }] : []),
      ];
  return [...list].sort((a, b) => b.at.localeCompare(a.at));
}

/** Which action is running, and how far through. */
type Busy = { action: "generate" | "send" | "delete"; done: number; total: number; ids: Set<string> };

// Estoppels — shaped like Tax Rolls: a tab per year, "+" to open this year,
// and the Template last, set apart, since it carries no year.
//
// A year's tab lists every tenant with its certificate and, in Actions, the
// eye (when it was generated and sent), Generate, Send to SMRT Docs and
// Delete for that tenant alone. Batch Actions swaps that for tick boxes and
// ONE row of the same buttons, acting on every tenant ticked.
export default function EstoppelsTab() {
  const { tenants, units, buildings, configuration, budgets } = useTospData();
  const { user } = useAuthentication();
  const byName = user?.name || user?.email || "";

  const [state, setState] = useState<EstoppelState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View | null>(null);
  const [batch, setBatch] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<Busy | null>(null);
  const [failures, setFailures] = useState<Record<string, string>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState<Row[] | null>(null);
  const [uploading, setUploading] = useState(false);
  const [opening, setOpening] = useState(false);

  const reload = useCallback(async () => {
    try {
      setState(await loadEstoppels());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't load estoppels");
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const thisYear = new Date().getFullYear();
  const years = state?.years ?? [];
  // Opens on the newest year; with none yet, the Template is the only place to be.
  const active: View = view ?? years[0] ?? "template";

  // Selection belongs to the year it was made in.
  useEffect(() => {
    setSelected(new Set());
    setFailures({});
    setBatch(false);
  }, [active]);

  const unitById = useCallback((id: string) => units.find((u) => u.id === id), [units]);

  const rows = useMemo<Row[]>(() => {
    if (typeof active !== "number") return [];
    return [...tenants]
      .sort((a, b) => a.operatingName.localeCompare(b.operatingName))
      .map((tenant) => ({
        tenant,
        draft: estoppelDraft({ tenant, year: active, unitById, buildings, configuration, budgets }),
        certificate: state?.certificates.find((c) => c.year === active && c.tenantId === tenant.id),
      }));
  }, [active, tenants, unitById, buildings, configuration, state]);

  // What each action can act on. A warning means something would print blank
  // or wrong, so no certificate is made or sent until it's fixed in TOSP.
  const ready = (row: Row) => Boolean(row.draft.values) && row.draft.problems.length === 0;
  const canGenerate = (row: Row) => ready(row) && Boolean(state?.template);
  const canSend = (row: Row) => ready(row) && Boolean(row.certificate);
  const canDelete = (row: Row) => Boolean(row.certificate);

  const toggleIn = (set: Set<string>, id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };

  // In batch mode anything with a certificate or ready to generate can be
  // ticked; each batch button then acts on whichever ticked rows it can.
  const tickable = rows.filter((row) => ready(row) || row.certificate);
  const allSelected = tickable.length > 0 && tickable.every((row) => selected.has(row.tenant.id));
  const chosen = rows.filter((row) => selected.has(row.tenant.id));

  /** Runs an action over rows in batches, collecting per-tenant failures. */
  const run = async (
    action: Busy["action"],
    targets: Row[],
    call: (batchRows: Row[]) => Promise<{ tenantId: string; error: string }[]>,
  ) => {
    if (typeof active !== "number" || targets.length === 0) return;
    setError(null);
    setFailures({});
    const ids = new Set(targets.map((r) => r.tenant.id));
    setBusy({ action, done: 0, total: targets.length, ids });
    const failed: Record<string, string> = {};
    try {
      for (let i = 0; i < targets.length; i += BATCH_SIZE) {
        const part = targets.slice(i, i + BATCH_SIZE);
        for (const f of await call(part)) failed[f.tenantId] = f.error;
        setBusy({ action, done: Math.min(i + BATCH_SIZE, targets.length), total: targets.length, ids });
      }
      if (batch) setSelected(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : "That didn't work");
    } finally {
      setFailures(failed);
      setBusy(null);
      await reload();
    }
  };

  const generate = (targets: Row[]) =>
    run("generate", targets.filter(canGenerate), async (part) =>
      (
        await generateEstoppels(
          active as number,
          part.map((row) => ({
            tenantId: row.tenant.id,
            fileName: estoppelFileName(active as number, row.tenant),
            values: row.draft.values,
          })),
          byName,
        )
      ).failed,
    );

  const send = (targets: Row[]) =>
    run("send", targets.filter(canSend), async (part) =>
      (await sendEstoppels(active as number, part.map((row) => row.tenant.id), byName)).failed,
    );

  const remove = (targets: Row[]) =>
    run("delete", targets.filter(canDelete), async (part) => {
      const failed: { tenantId: string; error: string }[] = [];
      for (const row of part) {
        try {
          await deleteEstoppel(active as number, row.tenant.id);
        } catch (err) {
          failed.push({ tenantId: row.tenant.id, error: err instanceof Error ? err.message : "Couldn't delete" });
        }
      }
      return failed;
    });

  const openYear = async () => {
    setOpening(true);
    try {
      await openEstoppelYear(thisYear);
      await reload();
      setView(thisYear);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't open the year");
    } finally {
      setOpening(false);
    }
  };

  const uploadTemplate = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    try {
      await uploadEstoppelTemplate(file, byName);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  if (!state && !error) {
    return (
      <section className="tosp-card">
        <div className="tosp-grid-empty">Loading…</div>
      </section>
    );
  }

  const yearOpen = years.includes(thisYear);
  const spinning = (action: Busy["action"], id?: string) =>
    busy?.action === action && (id === undefined || busy.ids.has(id));

  /**
   * The row of action buttons. Used once per tenant, and once for the batch —
   * where each button acts on every ticked tenant it can, and says how many.
   */
  const actionButtons = (targets: Row[], opts: { disabled: boolean; single?: Row }) => {
    const gen = targets.filter(canGenerate);
    const snd = targets.filter(canSend);
    const del = targets.filter(canDelete);
    const count = (n: number) => (opts.single ? "" : ` (${n})`);
    const id = opts.single?.tenant.id;
    return (
      <>
        {opts.single && (
          <button
            type="button"
            className="tosp-btn-icon tosp-btn-history"
            // Nothing to show until a certificate exists: greyed and inert.
            disabled={!opts.single.certificate}
            title={
              !opts.single.certificate
                ? "No certificate generated yet"
                : expanded.has(opts.single.tenant.id)
                  ? "Hide history"
                  : "History"
            }
            aria-expanded={expanded.has(opts.single.tenant.id)}
            onClick={() => setExpanded((prev) => toggleIn(prev, opts.single!.tenant.id))}
          >
            ↻
          </button>
        )}
        <button
          type="button"
          className="tosp-btn-icon tosp-btn-generate"
          title={
            opts.single && opts.single.draft.problems.length
              ? "Fix the warnings in TOSP before this certificate can be made"
              : `Generate Estoppel Certificate${count(gen.length)}`
          }
          disabled={opts.disabled || busy !== null || gen.length === 0}
          onClick={() => generate(targets)}
        >
          {spinning("generate", id) ? <span className="tosp-spinner" aria-hidden="true" /> : "▶"}
        </button>
        <button
          type="button"
          className="tosp-btn-icon tosp-btn-view"
          title={`Send to SMRT Docs${count(snd.length)}`}
          disabled={opts.disabled || busy !== null || snd.length === 0}
          onClick={() => send(targets)}
        >
          {spinning("send", id) ? <span className="tosp-spinner" aria-hidden="true" /> : <UploadIcon />}
        </button>
        <button
          type="button"
          className="tosp-btn-icon tosp-btn-delete"
          title={`Delete${count(del.length)}`}
          disabled={opts.disabled || busy !== null || del.length === 0}
          onClick={() => setDeleting(del)}
        >
          {spinning("delete", id) ? <span className="tosp-spinner" aria-hidden="true" /> : "🗑"}
        </button>
      </>
    );
  };

  return (
    <section className="tosp-card">
      <div className="tosp-year-tabs" role="tablist" aria-label="Estoppel years and template">
        {years.map((year) => (
          <button
            key={year}
            type="button"
            role="tab"
            aria-selected={active === year}
            className={`tosp-year-tab${active === year ? " tosp-year-tab-active" : ""}`}
            onClick={() => setView(year)}
          >
            {year}
          </button>
        ))}
        <button
          type="button"
          className="tosp-year-tab tosp-year-tab-plus"
          onClick={openYear}
          disabled={yearOpen || opening}
          title={yearOpen ? `${thisYear} is already open` : `Open ${thisYear}`}
        >
          {opening ? <span className="tosp-spinner" aria-hidden="true" /> : "+"}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={active === "template"}
          className={`tosp-year-tab tosp-year-tab-register${
            active === "template" ? " tosp-year-tab-active" : ""
          }`}
          onClick={() => setView("template")}
          title="The Word document every certificate is filled from"
        >
          Template
        </button>
      </div>

      {error && (
        <div className="tosp-banner tosp-banner-error">
          <span>{error}</span>
        </div>
      )}

      {active === "template" ? (
        <div className="tosp-estoppel-template">
          {/* The template itself, in a box of its own above the document. */}
          <div className="tosp-template-upload">
            <div className="tosp-template-upload-title">Template</div>
            {state?.template ? (
              <p>
                Current template:{" "}
                <a href={estoppelTemplateUrl()} target="_blank" rel="noreferrer">
                  {state.template.fileName}
                </a>
                <span className="tosp-field-hint">
                  {" "}
                  — uploaded {formatDate(state.template.uploadedAt.slice(0, 10))}
                  {state.template.uploadedByName ? ` by ${state.template.uploadedByName}` : ""}
                </span>
              </p>
            ) : (
              <p>No template yet. Upload the Word document the certificates are filled from.</p>
            )}

            <label className="tosp-btn-primary tosp-file-button" aria-disabled={uploading}>
              {uploading ? (
                <>
                  <span className="tosp-spinner" aria-hidden="true" />
                  Uploading…
                </>
              ) : state?.template ? (
                "Replace Template"
              ) : (
                "Upload Template"
              )}
              <input
                type="file"
                accept=".docx"
                hidden
                disabled={uploading}
                onChange={(e) => {
                  uploadTemplate(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            </label>

            <p className="tosp-field-hint" style={{ marginTop: 12 }}>
              {"A Word .docx. Put each field in square brackets, e.g. [LANDLORD LEGAL ENTITY], and " +
                "wording that should only appear when its field has a value in curly braces, e.g. " +
                "{ and $[STORAGE RENT] per annum for storage space}. Extra areas are numbered: " +
                "[AREA 2 SF], [AREA 2 LOCATION]."}
            </p>
          </div>

          {state?.template && <EstoppelTemplateEditor templateKey={state.template.uploadedAt} />}
        </div>
      ) : (
        <>
          <div className="tosp-toolbar">
            <button
              className={batch ? "tosp-btn-secondary" : "tosp-btn-primary"}
              disabled={busy !== null}
              onClick={() => {
                setBatch((on) => !on);
                setSelected(new Set());
              }}
              title={batch ? "Back to one tenant at a time" : "Tick several tenants and act on them together"}
            >
              {batch ? "Done" : "Batch Actions"}
            </button>
            {busy && (
              <span className="tosp-field-hint">
                {busy.action === "generate" ? "Generating" : busy.action === "send" ? "Sending" : "Deleting"}{" "}
                {busy.done} of {busy.total}…
              </span>
            )}
          </div>

          <div className={`tosp-grid-header tosp-estoppel-grid${batch ? " tosp-estoppel-batch" : ""}`}>
            {batch && (
              <div>
                <input
                  type="checkbox"
                  aria-label="Select all tenants"
                  checked={allSelected}
                  disabled={busy !== null || tickable.length === 0}
                  onChange={() =>
                    setSelected(allSelected ? new Set() : new Set(tickable.map((row) => row.tenant.id)))
                  }
                />
              </div>
            )}
            <div>Tenant</div>
            <div>Certificate</div>
            <div>Actions</div>
          </div>

          {/* Batch mode: one row of buttons, acting on everything ticked. */}
          {batch && (
            <div className="tosp-grid-row tosp-estoppel-grid tosp-estoppel-batch tosp-estoppel-batchbar">
              <div />
              <div className="tosp-field-hint">
                {chosen.length ? `${chosen.length} selected` : "Tick the tenants to act on"}
              </div>
              <div />
              <div className="tosp-actions">{actionButtons(chosen, { disabled: chosen.length === 0 })}</div>
            </div>
          )}

          {rows.map((row) => {
            const { tenant, draft, certificate } = row;
            return (
              <React.Fragment key={tenant.id}>
                <div className={`tosp-grid-row tosp-estoppel-grid${batch ? " tosp-estoppel-batch" : ""}`}>
                  {batch && (
                    <div>
                      <input
                        type="checkbox"
                        aria-label={`Select ${tenant.operatingName}`}
                        checked={selected.has(tenant.id)}
                        disabled={busy !== null || !(ready(row) || certificate)}
                        title={
                          draft.problems.length && !certificate
                            ? "Fix the warnings in TOSP before this certificate can be made"
                            : undefined
                        }
                        onChange={() => setSelected((prev) => toggleIn(prev, tenant.id))}
                      />
                    </div>
                  )}
                  <div className="tosp-cell-wrap">
                    {tenant.operatingName}
                    {draft.problems.length > 0 && (
                      <div className="tosp-estoppel-problems">⚠ {draft.problems.join(" · ")}</div>
                    )}
                    {failures[tenant.id] && <div className="tosp-field-error">{failures[tenant.id]}</div>}
                  </div>
                  <div className="tosp-cell-wrap">
                    {certificate ? (
                      <a href={estoppelFileUrl(certificate.id)} target="_blank" rel="noreferrer">
                        {certificate.fileName}
                      </a>
                    ) : (
                      "—"
                    )}
                  </div>
                  {/* Greyed out while Batch Actions is on: the batch row acts instead. */}
                  <div className="tosp-actions">{actionButtons([row], { disabled: batch, single: row })}</div>
                </div>

                {/* The certificate's history on one small line, newest first:
                    "Generated by X on … · Sent to SMRT Docs by X on …". */}
                {certificate && expanded.has(tenant.id) && (
                  <div className="tosp-estoppel-detail">
                    {historyOf(certificate)
                      .map(
                        (item) =>
                          `${item.status}${item.byName ? ` by ${item.byName}` : ""} on ${new Date(
                            item.at,
                          ).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}`,
                      )
                      .join(" · ")}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </>
      )}

      <DeleteConfirmModal
        isOpen={!!deleting}
        title={deleting && deleting.length > 1 ? "Delete Certificates" : "Delete Certificate"}
        message={
          deleting
            ? `Delete ${
                deleting.length === 1
                  ? `the certificate for ${deleting[0].tenant.operatingName}`
                  : `${deleting.length} certificates`
              }? The PDF is removed from TOSP. A copy already sent to SMRT Docs stays there.`
            : ""
        }
        confirmLabel="Delete"
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          const targets = deleting || [];
          setDeleting(null);
          remove(targets);
        }}
      />
    </section>
  );
}
